import { useState, useEffect } from "react";
import api from "../../services/api";
import Dialog from "./Dialog";

interface Topic {
  id: string;
  name: string;
}

export default function TopicManager() {
  const [topics, setTopics] = useState<Topic[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newTopicName, setNewTopicName] = useState("");
  const [editingTopic, setEditingTopic] = useState<Topic | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTopics = async () => {
    try {
      const res = await api.get("/topics");
      setTopics(res.data);
    } catch (err) {
      console.error("Failed to fetch topics:", err);
    }
  };

  useEffect(() => {
    fetchTopics();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTopicName.trim()) return;

    setIsProcessing(true);
    setError(null);
    try {
      await api.post("/topics", { name: newTopicName.trim() });
      setNewTopicName("");
      setIsModalOpen(false);
      fetchTopics();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to create topic");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUpdate = async (topicId: string, newName: string) => {
    if (!newName.trim()) return;

    setIsProcessing(true);
    try {
      await api.patch(`/topics/${topicId}`, { name: newName.trim() });
      setEditingTopic(null);
      fetchTopics();
    } catch (err) {
      console.error("Update failed:", err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Are you sure you want to delete "${name}"? Existing tasks using this topic will be unassigned.`)) return;

    try {
      await api.delete(`/topics/${id}`);
      fetchTopics();
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Failed to delete topic. It may be protected or in use.");
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
      {/* Minimalist Hero Header */}
      <div className="flex justify-between items-end pb-4 border-b border-outline-variant/10">
        <div>
          <h2 className="text-xl font-black text-on-surface tracking-tight">Topic Registry</h2>
          <p className="text-[9px] font-bold text-on-surface-variant uppercase tracking-[0.2em] mt-1">Core curriculum categorization assets</p>
        </div>
        <button 
          onClick={() => {
            setEditingTopic(null);
            setIsModalOpen(true);
            setError(null);
          }}
          className="flex items-center gap-2 px-6 py-2.5 bg-primary text-white rounded-xl font-black text-[9px] uppercase tracking-widest shadow-md shadow-primary/10 hover:shadow-lg active:scale-[0.98] transition-all"
        >
          <span className="material-symbols-outlined text-[14px]">add</span>
          Register Topic
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {topics.map((topic) => (
          <div 
            key={topic.id}
            className="group bg-surface-container-low border border-outline-variant/5 rounded-[1.5rem] p-5 transition-all hover:bg-surface-container-high flex flex-row items-center gap-6 relative overflow-hidden"
          >
            {/* Visual Accent */}
            <div className="absolute left-0 top-0 bottom-0 w-1 transition-all group-hover:w-2 bg-primary/20 group-hover:bg-primary" />
            
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-1">
                <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest opacity-40 italic">ID: {topic.id}</span>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-on-surface/5 flex items-center justify-center text-on-surface-variant group-hover:bg-primary/10 group-hover:text-primary transition-all">
                  <span className="material-symbols-outlined text-xl">category</span>
                </div>
                
                {editingTopic?.id === topic.id ? (
                  <input
                    autoFocus
                    className="flex-1 bg-surface-container-highest px-4 py-1.5 rounded-lg text-base font-black text-on-surface outline-none border-2 border-primary shadow-inner"
                    defaultValue={topic.name}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleUpdate(topic.id, (e.target as HTMLInputElement).value);
                      if (e.key === "Escape") setEditingTopic(null);
                    }}
                    onBlur={(e) => handleUpdate(topic.id, e.target.value)}
                  />
                ) : (
                  <h3 className="text-base font-black text-on-surface tracking-tight group-hover:text-primary transition-colors">
                    {topic.name}
                  </h3>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3 pl-6 border-l border-outline-variant/10 h-full">
              <button 
                onClick={() => setEditingTopic(topic)}
                className="p-4 rounded-xl bg-surface-container-low border border-outline-variant/10 text-on-surface-variant hover:text-primary hover:border-primary/20 transition-all shadow-sm flex items-center gap-2 group/btn"
                title="Rename Topic"
              >
                <span className="material-symbols-outlined text-xl transition-transform group-hover/btn:rotate-12">edit</span>
                <span className="text-[9px] font-black uppercase tracking-widest hidden lg:inline">Rename</span>
              </button>
              <button 
                onClick={() => handleDelete(topic.id, topic.name)}
                className="p-4 rounded-xl bg-surface-container-low border border-outline-variant/10 text-on-surface-variant hover:text-red-500 hover:border-red-500/20 transition-all shadow-sm flex items-center gap-2 group/del"
                title="Delete Topic"
              >
                <span className="material-symbols-outlined text-xl transition-transform group-hover/del:scale-110">delete</span>
                <span className="text-[9px] font-black uppercase tracking-widest hidden lg:inline">Delete</span>
              </button>
            </div>
          </div>
        ))}

        {topics.length === 0 && (
          <div className="py-20 text-center bg-surface-container-low border border-dashed border-outline-variant/30 rounded-[3rem] flex flex-col items-center gap-4">
            <span className="material-symbols-outlined text-6xl text-on-surface-variant/20 animate-pulse">category</span>
            <p className="text-xs font-black uppercase tracking-[0.2em] text-on-surface-variant">System Registry Empty</p>
          </div>
        )}
      </div>

      <Dialog
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Create New Topic"
      >
        <form onSubmit={handleCreate} className="space-y-6">
          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase tracking-widest text-on-surface-variant ml-4">Topic Name</label>
            <input
              required
              autoFocus
              value={newTopicName}
              onChange={(e) => setNewTopicName(e.target.value)}
              placeholder="e.g. Behavioral Psychology"
              className="w-full bg-surface-container-low border border-outline-variant/30 px-6 py-4 rounded-2xl focus:ring-2 focus:ring-primary outline-none transition-all font-bold text-sm"
            />
            {error && <p className="text-[10px] font-bold text-error ml-4">{error}</p>}
          </div>

          <button
            type="submit"
            disabled={isProcessing}
            className="w-full py-4 bg-primary text-on-primary rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50"
          >
            {isProcessing ? "Adding..." : "Register Topic"}
          </button>
        </form>
      </Dialog>
    </div>
  );
}
